import React, { useState, useRef, useCallback } from 'react';

/**
 * VoiceInput — self-contained voice-to-text component using Web Speech API.
 *
 * Props:
 *   onTranscript(text)  — called when speech is successfully transcribed
 *   lang                — BCP-47 language tag (e.g. 'en-US', 'pt-BR', 'es-ES')
 *   disabled            — mirrors the send button's disabled state
 *
 * Hides itself on browsers that don't support SpeechRecognition (no error shown).
 * Designed to be reused in future iOS/Android React Native adapters.
 */
export default function VoiceInput({ onTranscript, lang = 'en-US', disabled = false }) {
  const [isListening, setIsListening] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const recognitionRef = useRef(null);

  const SpeechRecognition =
    typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognition) return;
    if (isListening) { stop(); return; }

    setStatusMsg('');
    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      onTranscript(transcript);
      setStatusMsg('');
      stop();
    };

    recognition.onerror = (event) => {
      const errorMessages = {
        'not-allowed':    'Microphone access was denied',
        'no-speech':      'No speech detected, please try again',
        'network':        'Voice recognition unavailable, please type instead',
        'audio-capture':  'No microphone found',
        'aborted':        '',
      };
      setStatusMsg(errorMessages[event.error] || 'Voice recognition error');
      stop();
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [SpeechRecognition, isListening, lang, onTranscript, stop]);

  // Hide silently if not supported
  if (!SpeechRecognition) return null;

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
      <button
        type="button"
        onClick={start}
        disabled={disabled}
        title={isListening ? 'Stop recording' : 'Start voice input'}
        style={{
          background: isListening ? '#8B1A1A' : 'transparent',
          border: `1px solid ${isListening ? '#8B1A1A' : 'rgba(139,26,26,0.4)'}`,
          borderRadius: '50%',
          width: '36px',
          height: '36px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.2s',
          animation: isListening ? 'pulse 1.2s infinite' : 'none',
        }}
        aria-label={isListening ? 'Stop recording' : 'Start voice input'}
      >
        {/* Microphone icon */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isListening ? '#fff' : '#8B1A1A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="2" width="6" height="11" rx="3" />
          <path d="M5 10a7 7 0 0 0 14 0" />
          <line x1="12" y1="19" x2="12" y2="22" />
          <line x1="8" y1="22" x2="16" y2="22" />
        </svg>
      </button>
      {statusMsg && (
        <span style={{ fontSize: '10px', color: '#c0392b', whiteSpace: 'nowrap', maxWidth: '120px', textAlign: 'center' }}>
          {statusMsg}
        </span>
      )}
    </span>
  );
}
